import {
  bindClickEventOnPseudoElement,
  setTriggerSource,
  toggleDisplayOverlay,
} from './genericUtils';
import { keyCodes } from '../keyboardUtils/keyboardUtils';
export const openModal = (modalSelector: string) => {
  const modalElement = document.querySelector(`${modalSelector}`);
  if (!modalElement) return;

  const parent = modalElement.parentElement;
  parent?.classList.remove('hide');

  /**
   * The "-modal-fullscreen" modal uses `position: relative`, so it stays within the normal document flow and
   * by default appears near the bottom of the page.
   * This positioning means the modal scrolls naturally with the page content instead of remaining fixed in one place on the screen.
   * Applying a large negative `translateY` percentage shifts the modal upward by a multiple of its own height,
   * effectively moving it to the top of the viewport.
   * The fixed pixel offset acts like a top margin to adjust its vertical placement.
   * The following method adjusts for the modal's location in the DOM and its size relative to the entire page height.
   * The modal can expand freely in height without internal scrollbars while maintaining its relative position within the page flow.
   */
  if (parent?.classList.contains('-modal-fullscreen-scrollable')) {
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        const totalPageHeight = document.body.scrollHeight; // Including content outside the viewport
        const modalHeight = parent.clientHeight;
        const ratio = totalPageHeight / modalHeight - 1; // How many times the modal fits into the total page minus 1
        const percentage = Number(ratio.toFixed(2)) * 100;
        const offset = '25%';

        parent.style.transform = `translateY(calc(-${percentage}% + ${offset}))`;
        parent.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    resizeObserver.observe(parent);
    return;
  }

  requestAnimationFrame(() => {
    const body = parent?.querySelector<HTMLElement>('.-modal-components-container');
    body?.scrollTo(0, 0);
  });
};

export const openNewModal = (modalElement: HTMLElement) => {
  modalElement.setAttribute('open', '');
  const modalOverlay = modalElement.closest('.cmp-modal__overlay');
  if (modalOverlay) {
    modalOverlay.setAttribute('open', '');
  }
};

// create a closure that maintains the correct targetId for the listener and still allows it to be properly removed
export const addOverlay = (() => {
  const listenersMap = new Map<Element, EventListener>();

  return (targetId: string) => {
    const overlay = document?.getElementsByClassName('bg-overlay')[0];
    if (!overlay) {
      return;
    }

    // Check if a listener already exists for this overlay and remove it if necessary
    if (listenersMap.has(overlay)) {
      overlay.removeEventListener('click', listenersMap.get(overlay) as EventListener);
      listenersMap.delete(overlay);
    }

    const handleOverlayClick: EventListener = (event: Event) => {
      if (!document.querySelector(targetId)?.parentElement?.classList.contains('overlay-close'))
        return;
      event.stopImmediatePropagation();
      closeEverything(targetId);
    };

    // Add the event listener
    overlay.addEventListener('click', handleOverlayClick);
    listenersMap.set(overlay, handleOverlayClick);

    toggleDisplayOverlay();
  };
})();

export const closeEverything = (targetId: string) => {
  closeModal(targetId);
  removeOverlayOpacity(targetId);
  const body = document?.querySelector('body');
  if (body) {
    body.classList.remove('-overlay');
  }

  clearHashForModal(targetId);
  document.body.style.touchAction = 'auto';
};

export const manageModalButton = (targetId: string) => {
  const modalButtons = document?.querySelector(targetId)?.querySelectorAll('.button');
  modalButtons?.forEach((button) => {
    // check if button is not a call section button
    if (!button.classList.contains('cmp-call-section')) {
      button?.addEventListener('click', () => {
        closeEverything(targetId);
      });
    }
  });
};

function removeColonFromText(): void {
  document.querySelectorAll('.hide-colon').forEach((node) => {
    let text = (node as HTMLElement).textContent;
    if (text) {
      text = text?.replace(':', '').toLowerCase();
      (node as HTMLElement).textContent = text;
    }
  });
}

function pushHashForModal(targetId: string): void {
  const hash = targetId.startsWith('#') ? targetId : `#${targetId}`;
  if (window.location.hash !== hash) {
    history.pushState({ modal: hash }, '', hash);
  }
}

function clearHashForModal(targetId: string): void {
  const hash = targetId.startsWith('#') ? targetId : `#${targetId}`;
  if (window.location.hash === hash) {
    const url = new URL(window.location.href);
    url.hash = '';
    history.replaceState(history.state, '', url.toString());
  }
}

function addOverlayOpacity(modalSelector: string) {
  document.querySelector(`${modalSelector}`)?.parentElement?.classList.forEach((className) => {
    if (className.startsWith('opacity-')) {
      document?.querySelector('div.bg-overlay')?.classList.add(className);
    }
  });
}

function removeOverlayOpacity(modalSelector: string) {
  document.querySelector(`${modalSelector}`)?.parentElement?.classList.forEach((className) => {
    if (className.startsWith('opacity-')) {
      document?.querySelector('div.bg-overlay')?.classList.remove(className);
    }
  });
}

export const openEverything = (targetId: string) => {
  openModal(targetId);
  addOverlay(targetId);
  addOverlayOpacity(targetId);
  manageModalButton(targetId);
  closeByEscapeKey(targetId);
  removeColonFromText();

  //disable body scroll when modal is open
  document.body.style.touchAction = 'none';

  pushHashForModal(targetId);
};

export const closeModal = (modalSelector: string) => {
  document.querySelector(`${modalSelector}`)?.parentElement?.classList.add('hide');
  setTriggerSource('displayed'); // Reset to default or any appropriate value
};

function closeByEscapeKey(targetId: string) {
  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === keyCodes.ESCAPE) {
      event.preventDefault();

      closeEverything(targetId);
    }
  });
}

//function to initiate the pseudo elements of the modal base on the first child if it is a title
function initiateModalPseudoElements() {
  //Get all the modals
  const modals = document.querySelectorAll('.cmp-container__modal');

  modals.forEach((modal) => {
    //Check if the first child of the modal is a title
    //If the first child is a title add the js-modal-with-title class to the modal
    if (modal.classList.contains('js-modal-with-title')) {
      //And if closedbyx class is present, bind the click event on after pseudo element of the title
      bindClickEventOnPseudoElement(
        '.closedbyx .cmp-container .title',
        false,
        'after',
        handlePseudoElementClick
      );
    }
    //If the first child is not a title let the default behavior -position:absolute- to the pseudo element and bind its click event
    else {
      bindClickEventOnPseudoElement(
        '.cmp-container__modal',
        true,
        'before',
        handlePseudoElementClick
      );
    }
  });
}

//helper function to handle the click event on the pseudo element
const handlePseudoElementClick = (event: Event) => {
  const target = event.target as HTMLElement;

  //Case 1: If the pseudo element is positioned absolutely
  if (target.classList.contains('cmp-container__modal')) {
    const modalElement = target?.firstElementChild as HTMLElement;
    closeEverything('#' + modalElement?.id);
    const forms = modalElement.querySelectorAll('form');
    if (forms.length > 0) {
      forms.forEach((form) => {
        const dateInput = form.querySelector('.flatpickr-input') as HTMLInputElement & {
          _flatpickr?: any;
        };

        // Store minimum available date before resetting the form
        const { min } = dateInput;
        form.reset();

        if (dateInput?._flatpickr) {
          // Restore date value after form reset
          dateInput._flatpickr.setDate(min);
        }

        const elements = form.querySelectorAll('div');
        elements.forEach((element) => {
          element.classList.remove('error-input', 'error-select');
        });

        const errorValidation = form.querySelectorAll('.error-list-validation');
        errorValidation.forEach((parentDiv) => {
          const innerDivs = parentDiv.querySelectorAll('div');
          innerDivs.forEach((innerDiv) => {
            innerDiv.classList.add('input-hidden-error-messagge-vr');
          });
        });
      });
    }
    return;
  }

  //Case 2: If the pseudo element is within a title
  if (target.classList.contains('title')) {
    const modalElement = target?.parentElement as HTMLElement;
    closeEverything('#' + modalElement?.id);
    return;
  }
};

export const modalInitiliazation = () => {
  document.querySelectorAll('.cmp-button').forEach((item) => {
    let targetId: string | undefined;
    if (item.attributes.getNamedItem('href') != null) {
      targetId = item.attributes.getNamedItem('href')?.value;
    }

    if (!targetId) return;
    //check if the modal exist in page, in order to avoid opening overlay without modal
    const modalElement = document.getElementById(targetId.replace('#', ''));

    // make sure the targeted conatainer is actually a modal and not a regular container
    const isModalContainer =
      modalElement?.parentElement?.classList.contains('cmp-container__modal');

    if (
      typeof targetId !== 'undefined' &&
      targetId.charAt(0) === '#' &&
      modalElement &&
      modalElement.classList.contains('cmp-modal')
    ) {
      item.addEventListener('click', function () {
        openNewModal(modalElement);
      });
    } else if (
      typeof targetId !== 'undefined' &&
      targetId.charAt(0) === '#' &&
      modalElement &&
      isModalContainer
    ) {
      item.addEventListener('click', function () {
        const targetClasses = document.querySelector('body')?.classList;
        if (targetClasses?.contains('-overlay')) return;
        openEverything(targetId);
      });
    }
  });
};

export const modalOnDocumentReady = () => {
  modalInitiliazation();
  initiateModalPseudoElements();
};

if (document.readyState !== 'loading') {
  modalOnDocumentReady();
} else {
  document.addEventListener('DOMContentLoaded', modalOnDocumentReady);
}
